import type { OpenTab } from '@/components/features/document-tabs'
import {
  DEFAULT_ACTIVITY_VIEW,
  DEFAULT_TODOS_VIEW,
  type ActivityViewState,
  type TodosViewState,
} from '@/lib/basecamp-view-persist'
import { StorageKeys } from '@/lib/storage/keys'

/** Query keys each index may keep in the address bar. Foreign keys are stripped. */
export type IndexQueryOwner =
  | 'search'
  | 'todos'
  | 'activity'
  | 'browse'
  | 'drive'
  | 'none'

const SEARCH_KEYS = new Set(['q', 'view', 'path', 'user', 'semantic'])
const TODOS_KEYS = new Set(['mode', 'status', 'people', 'scope', 'q', 'person'])
const ACTIVITY_KEYS = new Set(['who', 'kind', 'linked', 'project'])
const BROWSE_KEYS = new Set(['q'])
const DRIVE_KEYS = new Set(['q'])

const TAB_ID_SECTION: Record<string, string> = {
  'basecamp-todos': 'todos',
  'basecamp-activity': 'activity',
  'basecamp-projects': 'projects',
  'basecamp-bookmarks': 'bookmarks',
  'basecamp-mystuff': 'mystuff',
}

export function resolveBasecampIndexSection(
  tab: Pick<OpenTab, 'id' | 'integrationData'>,
): string | undefined {
  const fromData = tab.integrationData?.section
  if (typeof fromData === 'string' && fromData) return fromData
  return TAB_ID_SECTION[tab.id]
}

export function indexQueryOwnerForTab(
  tab: Pick<OpenTab, 'id' | 'type' | 'integrationData'>,
): IndexQueryOwner {
  if (tab.type === 'search-view' || tab.type === 'edit-history') return 'search'
  if (tab.type === 'explorer') return 'browse'
  if (tab.type === 'integration-google-drive') return 'drive'
  if (tab.type === 'integration-basecamp') {
    const section = resolveBasecampIndexSection(tab)
    if (section === 'todos') return 'todos'
    if (section === 'activity') return 'activity'
  }
  return 'none'
}

export function indexQueryAllowlist(owner: IndexQueryOwner): Set<string> {
  if (owner === 'search') return SEARCH_KEYS
  if (owner === 'todos') return TODOS_KEYS
  if (owner === 'activity') return ACTIVITY_KEYS
  if (owner === 'browse') return BROWSE_KEYS
  if (owner === 'drive') return DRIVE_KEYS
  return new Set()
}

export function pickAllowlistedSearchParams(
  params: URLSearchParams,
  allow: Set<string>,
): URLSearchParams {
  const next = new URLSearchParams()
  allow.forEach((key) => {
    const value = params.get(key)
    if (value) next.set(key, value)
  })
  return next
}

export function hasForeignIndexQuery(params: URLSearchParams, allow: Set<string>): boolean {
  for (const key of params.keys()) {
    if (!allow.has(key)) return true
  }
  return false
}

/** Emit Todos share-link query. Defaults are omitted. */
export function todosQueryFromView(view: TodosViewState): URLSearchParams {
  const params = new URLSearchParams()
  if (view.listMode !== DEFAULT_TODOS_VIEW.listMode) params.set('mode', view.listMode)
  if (view.statusFilter !== DEFAULT_TODOS_VIEW.statusFilter) params.set('status', view.statusFilter)
  if (view.assigneeFacet !== DEFAULT_TODOS_VIEW.assigneeFacet) {
    params.set('people', view.assigneeFacet)
  }
  if (view.assigneeFacet === 'person' && view.assigneePerson) {
    params.set('person', view.assigneePerson)
  }
  if (view.scope !== DEFAULT_TODOS_VIEW.scope) params.set('scope', view.scope)
  const q = (view.q || '').trim()
  if (q) params.set('q', q)
  return params
}

export function activityQueryFromView(view: ActivityViewState): URLSearchParams {
  const params = new URLSearchParams()
  if (view.who !== DEFAULT_ACTIVITY_VIEW.who) params.set('who', view.who)
  if (view.kind !== DEFAULT_ACTIVITY_VIEW.kind) params.set('kind', view.kind)
  if (view.linkedOnly) params.set('linked', '1')
  if (view.projectId) params.set('project', view.projectId)
  return params
}

export function appendQuery(path: string, params: URLSearchParams): string {
  const qs = params.toString()
  return qs ? `${path}?${qs}` : path
}

export function pathnameOfUrl(url: string): string {
  const i = url.indexOf('?')
  return i >= 0 ? url.slice(0, i) : url
}

/** True when the bar is a Basecamp Todos index (share-link query owner). */
export function isBasecampTodosPath(pathname: string): boolean {
  return /(^|\/)basecamp\/todos\/?$/.test(pathname)
}

/** True when the bar is a Basecamp Activity index. */
export function isBasecampActivityPath(pathname: string): boolean {
  return /(^|\/)basecamp\/activity\/?$/.test(pathname)
}

/** True when the bar is the Search index. Search URLs are not workspace-prefixed. */
export function isSearchPath(pathname: string): boolean {
  return pathname === '/search' || pathname === '/search/'
}

export type SearchQueryWriteState = {
  query: string
  pathScope: string | null
  semantic: boolean
}

/**
 * Next Search query string, or null when this pathname must not be written.
 * A hidden Search tab stays mounted after open-from-search; writing there
 * stamps ?q= onto the document URL and fights URL-sync canonicalize.
 * Null also means the current /search query already matches.
 */
export function searchQueryParamsForPath(
  pathname: string,
  current: URLSearchParams,
  state: SearchQueryWriteState,
): URLSearchParams | null {
  if (!isSearchPath(pathname)) return null
  const nextParams = new URLSearchParams(current)
  nextParams.delete('view')
  const q = state.query.trim()
  if (q) nextParams.set('q', q)
  else nextParams.delete('q')
  if (state.pathScope) nextParams.set('path', state.pathScope)
  else nextParams.delete('path')
  if (state.semantic) nextParams.set('semantic', '1')
  else nextParams.delete('semantic')
  if (nextParams.toString() === current.toString()) return null
  return nextParams
}

export function searchOfUrl(url: string): string {
  const i = url.indexOf('?')
  return i >= 0 ? url.slice(i) : ''
}

export function canonicalizeIndexUrl(
  pathname: string,
  search: string,
  tab: Pick<OpenTab, 'id' | 'type' | 'integrationData'>,
): { url: string; stripped: boolean } {
  const allow = indexQueryAllowlist(indexQueryOwnerForTab(tab))
  const incoming = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const cleaned = pickAllowlistedSearchParams(incoming, allow)
  const stripped = hasForeignIndexQuery(incoming, allow)
  return { url: appendQuery(pathname, cleaned), stripped }
}

/** Keep the workspace slug in the bar; union allowlisted keys from location + tab emit. */
export function mergeIndexQueryForPath(
  pathname: string,
  locationSearch: string,
  targetUrl: string,
  tab: Pick<OpenTab, 'id' | 'type' | 'integrationData'>,
): string {
  const allow = indexQueryAllowlist(indexQueryOwnerForTab(tab))
  const loc = pickAllowlistedSearchParams(
    new URLSearchParams(locationSearch.startsWith('?') ? locationSearch.slice(1) : locationSearch),
    allow,
  )
  const target = pickAllowlistedSearchParams(
    new URLSearchParams(searchOfUrl(targetUrl).replace(/^\?/, '')),
    allow,
  )
  const merged = new URLSearchParams(loc)
  allow.forEach((key) => {
    if (!merged.get(key) && target.get(key)) merged.set(key, target.get(key) as string)
  })
  return appendQuery(pathname, merged)
}

export function todosQueryFromStorage(organizationId?: string): URLSearchParams {
  try {
    const raw = localStorage.getItem(StorageKeys.BASECAMP_TODOS_VIEW(organizationId))
    if (!raw) return new URLSearchParams()
    const parsed = JSON.parse(raw) as Partial<TodosViewState>
    return todosQueryFromView({
      ...DEFAULT_TODOS_VIEW,
      ...parsed,
    })
  } catch {
    return new URLSearchParams()
  }
}

export function activityQueryFromStorage(organizationId?: string): URLSearchParams {
  try {
    const raw = localStorage.getItem(StorageKeys.BASECAMP_ACTIVITY_VIEW(organizationId))
    if (!raw) return new URLSearchParams()
    const parsed = JSON.parse(raw) as Partial<ActivityViewState>
    return activityQueryFromView({
      ...DEFAULT_ACTIVITY_VIEW,
      ...parsed,
    })
  } catch {
    return new URLSearchParams()
  }
}
